/**
 * MCP Integration [Beta] — Settings tab (org_admin).
 *
 * Renders a permissions notice unless profile.mcp.enabled === true.
 * Plain API key is only held in local state right after generate — never re-fetched.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  KeyRound, Copy, Check, RefreshCw, Trash2, Sparkles, Shield, Terminal,
  Search, Users, Target, Clock, BarChart3, Layers, Workflow,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useDashTheme } from '../hooks/useDashTheme';
import { SectionCard, Button as DashButton } from '../components/primitives';

const KEY_PLACEHOLDER = 'YOUR_TEAMGRID_MCP_KEY';

// Tool list mirrors server/modules/mcp-server/tools.js. Blurbs stay one line.
const CAPABILITIES = [
  { id: 'get_work_summary', icon: BarChart3, accent: 'blue', title: 'Work summary', blurb: 'Daily scores, active time, top projects · 92d' },
  { id: 'get_team_productivity', icon: Users, accent: 'indigo', title: 'Team leaderboard', blurb: 'Rank the org by score and hours · 92d' },
  { id: 'search_employees', icon: Search, accent: 'aqua', title: 'Find people', blurb: 'Resolve names and emails to user IDs' },
  { id: 'get_hourly_detail', icon: Clock, accent: 'green', title: 'Hourly detail', blurb: 'Hour-by-hour for one person · 7d' },
  { id: 'get_sessions', icon: Layers, accent: 'yellowGreen', title: 'Sessions', blurb: 'Duration, app, project, screenshots · 31d' },
  { id: 'get_goals_status', icon: Target, accent: 'magenta', title: 'Goals', blurb: 'Goals with progress percentage' },
  { id: 'semantic_search', icon: Workflow, accent: 'purple', title: 'Semantic search', blurb: 'Natural-language search over activity' },
];

const TRUST = ['Read-only', 'Org-scoped', 'Rate-limited'];

function CopyButton({ value, label = 'Copy', size = 'sm' }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      toast.error('Copy failed — select and copy manually.');
    }
  };
  return (
    <DashButton
      type="button"
      variant="secondary"
      size={size}
      onClick={onCopy}
      leftIcon={copied ? <Check style={{ width: 13, height: 13 }} /> : <Copy style={{ width: 13, height: 13 }} />}
    >
      {copied ? 'Copied' : label}
    </DashButton>
  );
}

/** Paste-ready configs for common MCP clients + headless. */
function buildSnippets(endpoint, apiKey) {
  const url = endpoint || 'https://YOUR_HOST/mcp';
  const key = apiKey || KEY_PLACEHOLDER;
  const auth = `Bearer ${key}`;

  const headless = [
    `export TEAMGRID_MCP_URL="${url}"`,
    `export TEAMGRID_MCP_KEY="${key}"`,
    ``,
    `curl -sS -X POST "$TEAMGRID_MCP_URL" \\`,
    `  -H "Authorization: Bearer $TEAMGRID_MCP_KEY" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -H "Accept: application/json, text/event-stream" \\`,
    `  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list","params":{}}'`,
  ].join('\n');

  return [
    {
      id: 'universal',
      label: 'Universal',
      hint: 'Claude, Cursor, Codex — paste into mcpServers.',
      text: JSON.stringify({ mcpServers: { teamgrid: { url, headers: { Authorization: auth } } } }, null, 2),
    },
    {
      id: 'typed',
      label: 'HTTP',
      hint: 'Newer Claude Desktop / Cursor builds.',
      text: JSON.stringify({ mcpServers: { teamgrid: { type: 'http', url, headers: { Authorization: auth } } } }, null, 2),
    },
    {
      id: 'stdio',
      label: 'Stdio',
      hint: 'Hosts that only speak stdio.',
      text: JSON.stringify({ mcpServers: { teamgrid: { command: 'npx', args: ['-y', 'mcp-remote', url, '--header', `Authorization: ${auth}`] } } }, null, 2),
    },
    {
      id: 'headless',
      label: 'Headless',
      hint: 'CI, scripts, any agent runtime — no desktop app.',
      text: headless,
    },
  ];
}

/**
 * @param {object}   props
 * @param {object}   props.mcp        { enabled, hasKey, keyPrefix, createdAt, lastUsedAt, endpoint? }
 * @param {object}   props.api
 * @param {function} props.onChange
 */
export default function McpIntegrationCard({ mcp, api, onChange }) {
  const t = useDashTheme();
  const enabled = mcp?.enabled === true;
  const [busy, setBusy] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [generated, setGenerated] = useState(null); // { apiKey, endpoint }
  const [endpoint, setEndpoint] = useState(mcp?.endpoint || '');
  const [clientTab, setClientTab] = useState('universal');

  const hasKey = mcp?.hasKey === true;
  const liveKey = generated?.apiKey || null;
  const liveEndpoint = generated?.endpoint || endpoint || '';

  useEffect(() => {
    if (!enabled || !api) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/v1/organizations/mcp/status');
        const data = res?.data?.data?.mcp;
        if (cancelled || !data) return;
        if (data.endpoint) setEndpoint(data.endpoint);
        if (onChange && mcp) {
          onChange({
            ...mcp,
            hasKey: data.hasKey === true,
            keyPrefix: data.keyPrefix ?? mcp.keyPrefix,
            createdAt: data.createdAt ?? mcp.createdAt,
            lastUsedAt: data.lastUsedAt ?? mcp.lastUsedAt,
            endpoint: data.endpoint || mcp.endpoint,
          });
        }
      } catch (_) {
        /* status is best-effort; card still works from profile mcp */
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount refresh only
  }, [api, enabled]);

  const doGenerate = useCallback(async () => {
    setBusy(true);
    setConfirmRegen(false);
    try {
      const res = await api.post('/api/v1/organizations/mcp/generate-key');
      const data = res?.data?.data || {};
      const ep = data.endpoint || data.clientConfig?.mcpServers?.teamgrid?.url || endpoint;
      setGenerated({ apiKey: data.apiKey, endpoint: ep });
      if (ep) setEndpoint(ep);
      setClientTab('universal');
      toast.success('Key generated — copy a connector below.');
      if (onChange && mcp) {
        onChange({
          ...mcp,
          hasKey: true,
          keyPrefix: data.keyPrefix || mcp.keyPrefix,
          createdAt: data.createdAt || new Date().toISOString(),
          lastUsedAt: null,
          endpoint: ep || mcp.endpoint,
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to generate key');
    } finally {
      setBusy(false);
    }
  }, [api, endpoint, mcp, onChange]);

  const doRevoke = useCallback(async () => {
    setBusy(true);
    try {
      const res = await api.delete('/api/v1/organizations/mcp/key');
      const updated = res?.data?.data?.mcp;
      setGenerated(null);
      toast.success('API key revoked.');
      if (onChange && mcp) {
        onChange(updated || { ...mcp, hasKey: false, keyPrefix: null, createdAt: null, lastUsedAt: null, endpoint });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to revoke key');
    } finally {
      setBusy(false);
    }
  }, [api, endpoint, mcp, onChange]);

  const snippets = useMemo(() => buildSnippets(liveEndpoint, liveKey), [liveEndpoint, liveKey]);
  const activeSnippet = snippets.find((s) => s.id === clientTab) || snippets[0];

  const fmt = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString(); } catch (_) { return '—'; }
  };

  const iconTile = (Icon, accent, size = 28) => {
    const pair = t.accentPairs[accent] || t.accentPairs.blue;
    return (
      <span
        style={{
          width: size, height: size, borderRadius: t.r.sm, flexShrink: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: pair.bg, color: pair.fg,
        }}
      >
        <Icon style={{ width: size * 0.5, height: size * 0.5 }} />
      </span>
    );
  };

  const header = (sub) => (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <h2 style={{ margin: 0, fontSize: t.font.size.xl, fontWeight: 600, color: t.c.text }}>MCP Integration</h2>
        <span
          style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
            padding: '2px 7px', borderRadius: 999,
            background: t.accentPairs.purple.bg, color: t.accentPairs.purple.fg,
          }}
        >
          BETA
        </span>
      </div>
      <p style={{ margin: 0, fontSize: t.font.size.sm, color: t.c.textTertiary }}>{sub}</p>
    </div>
  );

  if (!enabled) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: t.s[4] }}>
        {header('Connect Claude, Cursor, Codex, or headless agents to your data.')}
        <SectionCard
          title="You don't have permission"
          subtitle="MCP isn't enabled for this organization"
          icon={<Shield style={{ width: 14, height: 14 }} />}
          accent="orange"
        >
          <p style={{ margin: 0, fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.5, maxWidth: 520 }}>
            A TeamGrid platform admin must enable MCP for your org. Ask them to switch it on, then refresh —
            key management and copy-paste connectors appear here.
          </p>
        </SectionCard>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: t.s[4] }}>
      {header('Generate a key, copy a connector, ask in plain language.')}

      {/* Connection */}
      <SectionCard
        title="Connection"
        subtitle={hasKey || generated ? 'Key active' : 'No key yet'}
        icon={<KeyRound style={{ width: 14, height: 14 }} />}
        accent="blue"
        actions={
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {liveEndpoint && <CopyButton value={liveEndpoint} label="Endpoint" />}
            {!hasKey && !generated && (
              <DashButton
                type="button"
                variant="primary"
                size="sm"
                disabled={busy}
                onClick={doGenerate}
                leftIcon={<Sparkles style={{ width: 13, height: 13 }} />}
              >
                {busy ? 'Generating…' : 'Generate key'}
              </DashButton>
            )}
          </div>
        }
      >
        {/* One-time key reveal */}
        {generated?.apiKey && (
          <div style={{ marginBottom: t.s[3] }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
              <code
                style={{
                  flex: '1 1 220px', padding: '7px 10px', borderRadius: t.r.sm,
                  border: `1px solid ${t.c.border}`,
                  background: t.isDark ? 'rgba(0,0,0,0.35)' : '#0f172a',
                  color: '#86efac', fontSize: 12, wordBreak: 'break-all',
                }}
              >
                {generated.apiKey}
              </code>
              <CopyButton value={generated.apiKey} label="Copy key" />
              <DashButton type="button" variant="ghost" size="sm" onClick={() => setGenerated(null)}>
                Dismiss
              </DashButton>
            </div>
            <div style={{ fontSize: t.font.size.xs, color: t.status.warning }}>
              Shown once. Connectors below already include it.
            </div>
          </div>
        )}

        {/* Facts row: endpoint + key meta, dense */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
            gap: t.s[3],
            alignItems: 'start',
          }}
        >
          {liveEndpoint && (
            <div style={{ gridColumn: 'span 2', minWidth: 0 }}>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: t.c.textTertiary }}>Endpoint</div>
              <code style={{ display: 'block', marginTop: 3, fontSize: 12, color: t.c.text, wordBreak: 'break-all' }}>
                {liveEndpoint}
              </code>
            </div>
          )}
          {hasKey && (
            <>
              <div>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: t.c.textTertiary }}>Key</div>
                <div style={{ marginTop: 3, fontSize: 13, color: t.c.text, fontFamily: 'ui-monospace, Menlo, monospace' }}>
                  {mcp.keyPrefix || 'tg_mcp_…'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: t.c.textTertiary }}>Created</div>
                <div style={{ marginTop: 3, fontSize: 13, color: t.c.text }}>{fmt(mcp.createdAt)}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: t.c.textTertiary }}>Last used</div>
                <div style={{ marginTop: 3, fontSize: 13, color: t.c.text }}>{mcp.lastUsedAt ? fmt(mcp.lastUsedAt) : 'Never'}</div>
              </div>
            </>
          )}
        </div>

        {/* Trust chips + lifecycle */}
        <div
          style={{
            marginTop: t.s[3], paddingTop: t.s[3], borderTop: `1px solid ${t.c.border}`,
            display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {TRUST.map((label) => (
              <span
                key={label}
                style={{
                  fontSize: 11, padding: '3px 8px', borderRadius: 999,
                  background: t.c.surfaceMuted, color: t.c.textSecondary,
                  border: `1px solid ${t.c.border}`,
                }}
              >
                {label}
              </span>
            ))}
          </div>

          {hasKey && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              {!confirmRegen ? (
                <DashButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={busy}
                  onClick={() => setConfirmRegen(true)}
                  leftIcon={<RefreshCw style={{ width: 13, height: 13 }} />}
                >
                  Regenerate
                </DashButton>
              ) : (
                <>
                  <span style={{ fontSize: 11, color: t.status.warning }}>Old key stops working.</span>
                  <DashButton type="button" variant="primary" size="sm" disabled={busy} onClick={doGenerate}>
                    {busy ? 'Working…' : 'Confirm'}
                  </DashButton>
                  <DashButton type="button" variant="ghost" size="sm" onClick={() => setConfirmRegen(false)}>
                    Cancel
                  </DashButton>
                </>
              )}
              <DashButton
                type="button"
                variant="danger"
                size="sm"
                disabled={busy}
                onClick={doRevoke}
                leftIcon={<Trash2 style={{ width: 13, height: 13 }} />}
              >
                Revoke
              </DashButton>
            </div>
          )}
        </div>
      </SectionCard>

      {/* Connectors */}
      <SectionCard
        title="Connectors"
        subtitle={liveKey ? 'Includes your key' : 'Placeholder key until you generate'}
        icon={<Terminal style={{ width: 14, height: 14 }} />}
        accent="purple"
        actions={<CopyButton value={activeSnippet.text} label="Copy" />}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: t.s[2] }}>
          {snippets.map((s) => {
            const active = s.id === activeSnippet.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setClientTab(s.id)}
                style={{
                  padding: '5px 11px', borderRadius: 999,
                  border: `1px solid ${active ? t.c.primary : t.c.border}`,
                  background: active ? t.c.primary : t.c.surface,
                  color: active ? '#fff' : t.c.textSecondary,
                  fontSize: t.font.size.xs, fontWeight: 600, cursor: 'pointer',
                  transition: 'background 160ms ease, border-color 160ms ease',
                }}
              >
                {s.label}
              </button>
            );
          })}
          <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, alignSelf: 'center', marginLeft: 4 }}>
            {activeSnippet.hint}
          </span>
        </div>

        <pre
          style={{
            margin: 0, padding: '10px 12px', borderRadius: t.r.sm,
            border: `1px solid ${t.c.border}`,
            background: t.isDark ? 'rgba(0,0,0,0.32)' : t.c.surfaceMuted,
            color: t.c.text, fontSize: 11.5, lineHeight: 1.45,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}
        >
          {activeSnippet.text}
        </pre>
      </SectionCard>

      {/* Capabilities */}
      <SectionCard
        title="Capabilities"
        subtitle="7 read-only tools"
        icon={<Sparkles style={{ width: 14, height: 14 }} />}
        accent="green"
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
            gap: t.s[2],
          }}
        >
          {CAPABILITIES.map((cap) => (
            <div key={cap.id} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', minWidth: 0, padding: '2px 0' }}>
              {iconTile(cap.icon, cap.accent)}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: t.c.text }}>{cap.title}</div>
                <div style={{ fontSize: 11.5, color: t.c.textTertiary, marginTop: 1, lineHeight: 1.35 }}>{cap.blurb}</div>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}
